import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, Input, InputNumber, Modal, Space, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type OrderDetail as Detail } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { SkuSelect } from "../catalog/mapping";

type Line = Detail["lines"][number];

function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };
}

/**
 * "Ganti barang": the line ships another SKU (another colour, out of stock).
 * The marketplace order stays as it is; the warehouse ships what is chosen.
 */
export function SwapLineModal({ orderId, line, onClose }: { orderId: string; line: Line | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [skuId, setSkuId] = useState<string | undefined>();
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orders/{orderID}/lines/{lineID}/swap", {
          params: { path: { orderID: orderId, lineID: line!.id } },
          body: { skuId: skuId!, note: note.trim() },
        }),
      ),
    onSuccess: () => {
      void message.success(t("orders.edit.swapped"));
      refresh();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={line !== null}
      title={t("orders.edit.swapTitle")}
      okText={t("orders.edit.swapOk")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !skuId }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      {line && (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            {t("orders.edit.swapHelp", { name: line.name, code: line.internalSkuCode || line.sellerSku, n: line.quantity })}
          </Typography.Text>
          <SkuSelect value={skuId} onChange={setSkuId} autoFocus />
          <Input maxLength={200} placeholder={t("orders.edit.note")} value={note} onChange={(e) => setNote(e.target.value)} />
        </Space>
      )}
    </Modal>
  );
}

type Row = { key: number; skuId?: string; quantity: number; unitPrice: number };

/** "Ubah barang" of a manual order: quantities, lines added or removed; the total follows. */
export function ManualLinesModal({ orderId, lines, open, onClose }: { orderId: string; lines: Line[]; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [rows, setRows] = useState<Row[]>(() =>
    lines
      .filter((l) => !l.cancelled && l.role === "item" && l.internalSkuId)
      .map((l, i) => ({ key: i, skuId: l.internalSkuId!, quantity: l.quantity, unitPrice: l.unitPrice })),
  );
  const set = (key: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const ready = rows.length > 0 && rows.every((r) => r.skuId && r.quantity > 0);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/v1/orders/{orderID}/manual-lines", {
          params: { path: { orderID: orderId } },
          body: { lines: rows.map((r) => ({ skuId: r.skuId!, quantity: r.quantity, unitPrice: r.unitPrice })) },
        }),
      ),
    onSuccess: () => {
      void message.success(t("orders.edit.linesSaved"));
      refresh();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={open}
      width={760}
      title={t("orders.edit.linesTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !ready }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        {rows.map((r) => (
          <Flex key={r.key} gap={8} align="center">
            <div style={{ flex: 1, minWidth: 0 }}>
              <SkuSelect value={r.skuId} onChange={(v) => set(r.key, { skuId: v })} />
            </div>
            <InputNumber min={1} max={100_000} precision={0} value={r.quantity} onChange={(v) => set(r.key, { quantity: v ?? 1 })} style={{ width: 90 }} />
            <InputNumber min={0} precision={0} prefix="Rp" value={r.unitPrice} onChange={(v) => set(r.key, { unitPrice: v ?? 0 })} style={{ width: 150 }} />
            <Button icon={<DeleteOutlined />} danger disabled={rows.length === 1} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
          </Flex>
        ))}
        <Button icon={<PlusOutlined />} onClick={() => setRows((rs) => [...rs, { key: Math.max(0, ...rs.map((x) => x.key)) + 1, quantity: 1, unitPrice: 0 }])}>
          {t("orders.edit.addLine")}
        </Button>
        <Typography.Text type="secondary">{t("orders.edit.linesHelp")}</Typography.Text>
      </Space>
    </Modal>
  );
}
